import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { eventsApi } from "../services/api";
import EventCard from "../components/EventCard";
import config from "../config";

function HostPortal() {
  const { hostId } = useParams();
  const [host, setHost] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const API = config.apiUrl;

    fetch(`${API}/hosts/${hostId}`)
      .then(r => r.ok ? r.json() : null)
      .then(hostData => {
        if (!hostData || !hostData.casinoName) {
          setHost({ hostId, casinoName: "", hostName: "", description: "", location: "", email: "", phone: "", website: "", logoUrl: "", bannerUrl: "" });
          setEvents([]);
        } else {
          setHost(hostData);
          const realHostId = hostData.hostId || hostId;
          eventsApi.list({ hostId: realHostId })
            .then(data => setEvents(Array.isArray(data) ? data.filter(e => e.status !== "archived") : []))
            .catch(() => setEvents([]));
        }
      })
      .catch(() => {
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [hostId]);

  if (loading) {
    return <div className="container" style={{ padding: "80px 0", color: "var(--text-light)" }}>Loading...</div>;
  }

  if (notFound || !host) {
    return (
      <div className="container" style={{ padding: "80px 0", textAlign: "center" }}>
        <h2>Host not found</h2>
        <p style={{ color: "var(--text-secondary)", margin: "12px 0 24px" }}>This host portal doesn't exist or has been removed.</p>
        <Link to="/" className="btn btn-primary">Back to Home</Link>
      </div>
    );
  }

  const hasProfile = host.casinoName && host.casinoName.trim().length > 0;

  return (
    <>
      {/* Host branded hero */}
      <section className="host-hero" style={host.bannerUrl ? { backgroundImage: `url(${host.bannerUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
        <div className="host-hero-overlay" />
        <div className="container host-hero-content">
          {host.logoUrl && <img src={host.logoUrl} alt={`${host.casinoName} logo`} className="host-logo" />}
          <h1>{hasProfile ? host.casinoName : "Host Portal"}</h1>
          {host.location && <p className="host-location">📍 {host.location}</p>}
          {host.description && <p className="host-description">{host.description}</p>}
          {!hasProfile && (
            <p className="host-description">This host hasn't set up their portal yet. Check back soon.</p>
          )}
          {hasProfile && (
            <div className="host-meta">
              {host.website && (
                <a href={host.website} target="_blank" rel="noopener noreferrer" className="host-meta-link">
                  🌐 Website
                </a>
              )}
              {host.phone && <span className="host-meta-item">📞 {host.phone}</span>}
              {host.email && <span className="host-meta-item">✉️ {host.email}</span>}
            </div>
          )}
        </div>
      </section>

      {/* Host's events */}
      <section className="section" style={{ background: "var(--white)" }}>
        <div className="container">
          <div style={{ marginBottom: "32px" }}>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.3px", marginBottom: "6px" }}>
              Available Experiences
            </h2>
            <p style={{ color: "var(--text-secondary)" }}>
              {hasProfile
                ? `Browse comp'd events offered by ${host.casinoName}`
                : "Events from this host"}
            </p>
          </div>

          {events.length === 0 ? (
            <div className="empty-state">
              <p>No events currently available. Check back soon.</p>
            </div>
          ) : (
            <div className="events-grid">
              {events.map(event => (
                <EventCard key={event.eventId} event={event} ctaText="Submit Inquiry →" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About the host — only show if profile is set up */}
      {hasProfile && (
        <section className="section">
          <div className="container">
            <div className="host-about-grid">
              <div className="host-about-card">
                <h3>About {host.casinoName}</h3>
                <p>{host.description}</p>
                {host.hostName && (
                  <div className="host-contact-card">
                    {host.profilePhotoUrl ? (
                      <img src={host.profilePhotoUrl} alt={host.hostName} className="host-contact-photo" />
                    ) : (
                      <div className="host-contact-avatar">
                        {host.hostName.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 600 }}>{host.hostName}</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>Your Host</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="host-about-card">
                <h3>How to Get Comp'd</h3>
                <div className="host-steps">
                  <div className="host-step">
                    <div className="host-step-num">1</div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>Choose an Event</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>Browse available experiences above</div>
                    </div>
                  </div>
                  <div className="host-step">
                    <div className="host-step-num">2</div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>Submit Your Info</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>Share your play history and current offers</div>
                    </div>
                  </div>
                  <div className="host-step">
                    <div className="host-step-num">3</div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>Get Confirmed</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>We'll review and confirm within 24-48 hours</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default HostPortal;
